import { Excel, t } from '@common';
import { ErrorCellRenderer } from '../render';
import { Parser } from './parser';
import type { Definition } from '@common';
import type { ColDef, ValueGetterParams } from 'ag-grid-enterprise';
import type { CellValue, RowValue } from '../types';

export class ImporterService {

  private static readonly INDEX_KEY = '__index';
  private static readonly VALID_KEY = '__valid';
  private static readonly VALID_REASON_KEY = '__reason';
  private readonly cell = new Parser();

  async parseValue(file: Blob | File, definitions: Definition[]): Promise<RowValue[]> {
    const [worksheet] = await new Excel().read(file);
    // TODO: excel 相关异常处理 ：excel文件 格式不对等
    if (worksheet == null) {
      return [];
    }

    const values: RowValue[] = [];
    const excelData: string[][] = [];
    worksheet.eachRow((row, rowNumber) => {
      // 如果不是数据行，不处理
      if (!this.isData(rowNumber)) {
        return;
      }

      const values = [];
      let count = 0;
      while (count < row.cellCount) {
        count += 1;
        values.push(row.getCell(count)?.text);
      }
      excelData.push(values);
    });

    for (let rowNumber = 0; rowNumber < excelData.length; rowNumber += 1) {
      const error = new Set<string>();
      const data: RowValue = {};
      values.push(data);

      for (let colNumber = 0; colNumber < definitions.length; colNumber += 1) {
        await this.parseCell(excelData, definitions, rowNumber, colNumber, error, data);
        // 设置行号
        data[ImporterService.INDEX_KEY] = { value: rowNumber + 1 };

        // 校验数据
        data[ImporterService.VALID_KEY] = { value: error.size === 0 };
        data[ImporterService.VALID_REASON_KEY] = { value: Array.from(error).join('，'), error };
      }
    }

    return values;
  }

  /**
   * 解析单元格数据
   */
  // eslint-disable-next-line max-len
  private async parseCell(rows: string[][], definitions: Definition[], rowNumber: number, colNumber: number, error: Set<string>, data: RowValue): Promise<void> {
    const parsed = await this.cell.parse(rows, definitions, rowNumber, colNumber);
    const definition = definitions[colNumber];

    // 保存数据
    if (parsed.error.size > 0) {
      Array.from(parsed.error).forEach(item => error.add(item));
      data[definition.key] = { value: parsed.text, error: parsed.error };
    } else {
      data[definition.key] = { value: parsed.value ?? '' };
    }
  }

  /**
   * TODO: 优化 根据标题行自动判断
   */
  private isData(row: number): boolean {
    return row > 1;
  }

  private valueGetter(params: ValueGetterParams<RowValue, CellValue['value']>): CellValue['value'] {
    const def = params.colDef;
    if (def.field == null || params.data?.[def.field] == null) {
      return '';
    }
    return params.data[def.field].value;
  }

  parseTitle(definitions: Definition[]): ColDef[] {
    const defs: ColDef[] = [];

    defs.push({
      field: ImporterService.INDEX_KEY,
      valueGetter: this.valueGetter,
      headerName: t('行'),
      width: 60,
      pinned: 'left',
      sortable: false
    });

    for (const definition of definitions) {
      const def: ColDef = {
        field: definition.key,
        valueGetter: this.valueGetter,
        headerName: definition.name,
        sortable: false
      };

      defs.push(def);
    }

    defs.push({
      field: ImporterService.VALID_KEY,
      valueGetter: this.valueGetter,
      headerName: t('验证'),
      width: 60,
      pinned: 'right',
      cellRenderer: ErrorCellRenderer,
      resizable: false,
      sortable: false
    });

    defs.push({
      field: ImporterService.VALID_REASON_KEY,
      valueGetter: this.valueGetter,
      headerName: t('异常原因'),
      width: 200,
      pinned: 'right',
      sortable: false
    });

    return defs;
  }

}
